/**
 * WelcomePage - Step 1 of App Orientation Flow
 * 
 * For users joining an organization that already has connections.
 * Shows overview of connected data sources and sets expectations.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { connectionDetection } from '../../services/connectionDetectionService';
import { userAPI } from '../../api/enhancedApiService';
import { 
  CheckCircleIcon, 
  ServerIcon, 
  ChartBarIcon,
  UsersIcon,
  ClockIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import Logo from '../../components/common/Logo';

const WelcomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [connectionSummary, setConnectionSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgInfo, setOrgInfo] = useState(null);

  useEffect(() => {
    const loadWelcomeData = async () => {
      try {
        // Get connection summary
        const connections = await connectionDetection.getConnectionSummary();
        setConnectionSummary(connections);

        // Get organization info with real data
        setOrgInfo({
          name: user?.user_metadata?.organization_name || 
                connections[0]?.organization_name ||
                'Your Organization',
          userCount: user?.user_metadata?.team_size || '3-5', // Use team size from onboarding
          setupDate: connections[0]?.created_at || new Date().toISOString()
        });

      } catch (error) {
        console.error('[Welcome] Error loading data:', error);
      }
      setLoading(false);
    };

    loadWelcomeData();
  }, [user]);

  const handleContinue = async () => {
    try {
      // Start onboarding tracking in backend
      await userAPI.startOnboarding('app_orientation');
      
      // Update progress with initial step
      await userAPI.updateOnboardingProgress({
        current_step: 'welcome',
        steps_completed: ['welcome'],
        completion_rate: 20, // 1 of 5 steps
        organization_info: orgInfo,
        connection_count: connectionSummary.length
      });
      
      console.log('[Welcome] Started onboarding tracking');
    } catch (error) {
      console.error('[Welcome] Error tracking progress:', error);
      // Continue with navigation even if tracking fails
    }
    
    navigate('/welcome/data');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Logo size="medium" withText={true} />
            <div className="text-sm text-gray-500">Welcome Tour • Step 1 of 5</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-6">
            <UsersIcon className="w-8 h-8 text-white" />
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Welcome to {orgInfo?.name}'s Sparvi workspace!
          </h1>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Your team has already set up data monitoring. Let's get you familiar with 
            your data landscape and show you how to navigate everything.
          </p>
        </div>

        {/* Current Data Overview */}
        <div className="bg-white rounded-lg shadow-sm border p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">What's Already Connected</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Connection Count */}
            <div className="bg-blue-50 rounded-lg p-6 text-center">
              <ServerIcon className="w-8 h-8 text-blue-600 mx-auto mb-3" />
              <div className="text-3xl font-bold text-blue-900">{connectionSummary.length}</div>
              <div className="text-blue-700 text-sm">Data Source{connectionSummary.length !== 1 ? 's' : ''}</div>
            </div>

            {/* Team Size */}
            <div className="bg-green-50 rounded-lg p-6 text-center">
              <UsersIcon className="w-8 h-8 text-green-600 mx-auto mb-3" />
              <div className="text-3xl font-bold text-green-900">
                {typeof orgInfo?.userCount === 'string' ? orgInfo.userCount : `${orgInfo?.userCount || '3-5'}`}
              </div>
              <div className="text-green-700 text-sm">Team Size</div>
            </div>

            {/* Time Active */}
            <div className="bg-purple-50 rounded-lg p-6 text-center">
              <ClockIcon className="w-8 h-8 text-purple-600 mx-auto mb-3" />
              <div className="text-3xl font-bold text-purple-900">
                {orgInfo?.setupDate ? Math.ceil((new Date() - new Date(orgInfo.setupDate)) / (1000 * 60 * 60 * 24)) : '1+'}
              </div>
              <div className="text-purple-700 text-sm">Days Active</div>
            </div>
          </div>

          {/* Connected Databases */}
          {connectionSummary.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Connected Data Sources</h3>
              <div className="space-y-3">
                {connectionSummary.map((conn, index) => (
                  <div key={conn.id} className="flex items-center p-4 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <ServerIcon className="w-5 h-5 text-blue-600" />
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <div className="text-sm font-medium text-gray-900">{conn.name}</div>
                      <div className="text-sm text-gray-500">{conn.type} • Connected {new Date(conn.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* What's Next */}
        <div className="bg-white rounded-lg shadow-sm border p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">What You'll Learn</h2>
          
          <div className="space-y-4 mb-8">
            <div className="flex items-start">
              <CheckCircleIcon className="w-6 h-6 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-gray-900">Navigate Your Data Landscape</h3>
                <p className="text-gray-600">Explore connected databases, understand data structure, and see quality scores.</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <CheckCircleIcon className="w-6 h-6 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-gray-900">Master Key Features</h3>
                <p className="text-gray-600">Dashboard navigation, data explorer, validation results, and issue management.</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <CheckCircleIcon className="w-6 h-6 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-gray-900">Get Hands-On Practice</h3>
                <p className="text-gray-600">Complete practical tasks with your actual data to build confidence.</p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-6">
            <div className="flex items-center">
              <ChartBarIcon className="w-6 h-6 text-blue-600 mr-3" />
              <div>
                <h3 className="font-medium text-blue-900">Estimated Time: 8-10 minutes</h3>
                <p className="text-blue-700 text-sm">Interactive tour with real data examples</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center mt-12">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-gray-500 hover:text-gray-700 text-sm font-medium"
          >
            Skip and go to dashboard
          </button>
          
          <button
            onClick={handleContinue}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Let's Get Started
            <ArrowRightIcon className="ml-2 h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomePage;